var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useQuery } from '@tanstack/react-query';
import { useDareDropApi } from 'root/src/client/v2/common/hooks/useDareDropApi';
import { GET_CREATOR_POINTS_TRANSACTIONS } from 'root/src/shared/descriptions/endpoints/endpointIds';
import { sortTimeEnum } from 'root/src/shared/constants/enums';
import { paypalPointsPayoutPendingKey, paypalPointsPayoutDoneKey, paidOffPlatformKey, pointsCustomKey, } from 'root/src/shared/constants/keys';
import { dropEventKey } from 'root/src/shared/constants/dropEvent';
import { PointsTransactionType } from 'root/src/server/modules/points/types/types';
import { usdRatio } from 'root/src/shared/constants/pointsCashOut';
import { formatNumberWithCommas } from 'root/src/shared/util/formatNumberWithCommas';
// Human labels per transaction `type`. Redesign copy: cleaner than the legacy
// creatorActivityDetailsList strings, with the payout variants sharing a "Payout (...)" family.
export const POINTS_TRANSACTION_LABELS = {
    [paypalPointsPayoutDoneKey]: 'Payout (Paid)',
    [paypalPointsPayoutPendingKey]: 'Payout (Pending)',
    [dropEventKey]: 'Drop',
    [paidOffPlatformKey]: 'Off-platform Payout',
    [pointsCustomKey]: 'Custom Points',
    [PointsTransactionType.QUEUED_PAYOUT]: 'Payout (Queued)',
};
export const getTransactionLabel = (type) => { var _a; return (_a = POINTS_TRANSACTION_LABELS[type]) !== null && _a !== void 0 ? _a : 'Transaction'; };
// USD value of a points balance, formatted exactly like the legacy PointsCashOutModal:
// `$` + formatNumberWithCommas of the `.toFixed(2)` value (Intl drops a whole-dollar `.00`).
export const formatPointsCashValue = (points) => (`$${formatNumberWithCommas(Number((points * usdRatio).toFixed(2)))}`);
// Payout-style rows (points leaving the balance) vs award-style rows (points earned).
export const isPayoutTransaction = (type) => (type === paypalPointsPayoutDoneKey
    || type === paypalPointsPayoutPendingKey
    || type === paidOffPlatformKey
    || type === PointsTransactionType.QUEUED_PAYOUT);
// Time-range filter (legacy sortTimeEnum): Lifetime first so the full history shows by default.
export const TRANSACTION_SECTION_OPTIONS = [
    { value: String(sortTimeEnum.LIFETIME), label: 'Lifetime' },
    { value: String(sortTimeEnum.THREEDAYS), label: 'Last 3 Days' },
    { value: String(sortTimeEnum.SEVENDAYS), label: 'Last 7 Days' },
    { value: String(sortTimeEnum.LASTMONTH), label: 'Last Month' },
];
export const DEFAULT_TRANSACTION_SECTION = String(sortTimeEnum.LIFETIME);
const STALE_TIME_MS = 1000 * 30;
// Fetches the creator's points transaction history for a time-range `section`, cached per section.
// `GET_CREATOR_POINTS_TRANSACTIONS` is a JS gateway action, so its payload/response type is `any`;
// we shape the result to `PointsTransactionRow[]` and sort newest-first for display.
export const usePointsTransactions = (section, enabled = true) => {
    var _a;
    const { fetcher } = useDareDropApi();
    const query = useQuery({
        queryKey: [GET_CREATOR_POINTS_TRANSACTIONS, section],
        queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
            var _b;
            // Read-only: suppress the fetcher's built-in legacy toast; the query's error state renders the UI.
            const res = yield fetcher({
                endpointId: GET_CREATOR_POINTS_TRANSACTIONS,
                // The handler is a JS gateway action so its payload type resolves to `undefined`; the
                // section is sent at runtime and cast away here (same pattern as the points endpoints).
                payload: { section },
            }, true);
            const rows = (_b = res) !== null && _b !== void 0 ? _b : [];
            return [...rows].sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
        }),
        enabled,
        staleTime: STALE_TIME_MS,
    });
    return {
        transactions: (_a = query.data) !== null && _a !== void 0 ? _a : [],
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        isError: query.isError,
    };
};
